const STORAGE_KEY = 'ss_supabase_circuit_v1';
const FAILURE_THRESHOLD = 3;
const COOLDOWN_MS = 30 * 1000;

function now() {
  return Date.now();
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function readState() {
  const fallback = { failures: 0, opened_until: 0 };
  const store = storage();
  if (!store) return fallback;
  try {
    return { ...fallback, ...(JSON.parse(store.getItem(STORAGE_KEY) || '{}') || {}) };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Best-effort only.
  }
}

export function isSupabaseTransientError(error) {
  if (isSupabaseCircuitError(error)) return false;
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('abort') ||
    message.includes('522') ||
    message.includes('echeckouttimeout') ||
    message.includes('ecircuitbreaker') ||
    message.includes('connection') ||
    message.includes('pool')
  );
}

export function isSupabaseCircuitError(error) {
  return error?.code === 'SUPABASE_CIRCUIT_OPEN' || error?.name === 'SupabaseCircuitOpenError';
}

export function getSupabaseCircuitState() {
  const state = readState();
  const remainingMs = Math.max(0, Number(state.opened_until || 0) - now());
  return {
    failures: Number(state.failures || 0),
    opened_until: Number(state.opened_until || 0),
    open: remainingMs > 0,
    remaining_ms: remainingMs,
  };
}

export function shouldShortCircuitSupabase() {
  return getSupabaseCircuitState().open;
}

export function noteSupabaseSuccess() {
  writeState({ failures: 0, opened_until: 0 });
}

export function noteSupabaseFailure(error) {
  if (!isSupabaseTransientError(error)) return getSupabaseCircuitState();

  const state = readState();
  const failures = Number(state.failures || 0) + 1;
  const openedUntil = failures >= FAILURE_THRESHOLD
    ? now() + COOLDOWN_MS
    : Number(state.opened_until || 0);
  const next = { failures, opened_until: openedUntil };
  writeState(next);
  return getSupabaseCircuitState();
}

export function supabaseCircuitError() {
  const state = getSupabaseCircuitState();
  const seconds = Math.max(1, Math.ceil(state.remaining_ms / 1000));
  const error = new Error(`Supabase is cooling down after repeated timeouts. Retrying in ${seconds}s.`);
  error.name = 'SupabaseCircuitOpenError';
  error.code = 'SUPABASE_CIRCUIT_OPEN';
  error.retryAfterMs = state.remaining_ms;
  return error;
}

export function resetSupabaseCircuit() {
  noteSupabaseSuccess();
}
